import { readErrorMessage } from "../../utils/apiError";
import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../utils/constants";
import LoadingSpinner from "../../components/LoadingSpinner";
import ClearForm from "../../components/Clearform";
import { Button, Form } from "react-bootstrap";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import CustomStepper from "../../components/CustomStepper";
import { useAuth } from "../../components/AuthProvider";
import { generateTimeOptions } from "../../utils/timeUtils";

const ROWS = 10;

const PageButtons = ({ total, current, onChange }) =>
  total <= 1 ? null : (
    <div className="d-flex justify-content-center mt-2">
      <nav>
        <ul className="pagination mb-0">
          {Array.from({ length: total }, (_, i) => (
            <li key={i} className={`page-item ${current === i + 1 ? "active" : ""}`}>
              <button className="page-link" onClick={() => onChange(i + 1)}>
                {i + 1}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );

const paginate = (arr, page) => arr.slice((page - 1) * ROWS, page * ROWS);
const pageCount = (arr) => Math.ceil(arr.length / ROWS);

const GenerateAccLtr = () => {
  const { user } = useAuth();

  // ── Core state ────────────────────────────────────────────────────────────
  const [arbId, setArbId]   = useState("");
  const [step, setStep]     = useState(0); // 0=lots  1=configure  2=pdf  3=done
  const [loading, setLoading] = useState(false);

  // ── Lots (step 0) ─────────────────────────────────────────────────────────
  const [lots, setLots]         = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [lotsPage, setLotsPage] = useState(1);
  const [selectedLot, setSelectedLot] = useState(null);

  // ── Cases (step 1) ────────────────────────────────────────────────────────
  const [cases, setCases]       = useState([]);
  const [casesPage, setCasesPage] = useState(1);

  // ── Zoom meetings ─────────────────────────────────────────────────────────
  const [meetings, setMeetings]             = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState("");
  const [joinUrl, setJoinUrl]   = useState("");
  const [zoomId, setZoomId]     = useState("");
  const [password, setPassword] = useState("");
  const [meetStartTime, setMeetStartTime] = useState("");

  // ── Schedule config ───────────────────────────────────────────────────────
  const [startTime, setStartTime]           = useState("");
  const [endTime, setEndTime]               = useState("");
  const [timeDifference, setTimeDifference] = useState(null);
  const [rate, setRate]                     = useState("");
  const [noOfCases, setNoOfCases]           = useState("");
  const [noOfAwardPassCases, setNoOfAwardPassCases] = useState("");

  // ── Distributed records (step 1 result) ──────────────────────────────────
  const [distRecords, setDistRecords] = useState([]);
  const [distPage, setDistPage]       = useState(1);

  // ── Slots other lots already hold for this arbitrator on the meeting's date ──
  const [bookedSlots, setBookedSlots] = useState([]);

  // ── Validation errors ─────────────────────────────────────────────────────
  const [formErrors, setFormErrors] = useState({});

  // ── PDF (step 2) ──────────────────────────────────────────────────────────
  const [pdfUrl, setPdfUrl] = useState("");

  const timeOptions = generateTimeOptions();

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (user?.[0]?.Ref_id) setArbId(user[0].Ref_id);
  }, [user]);

  useEffect(() => {
    if (!arbId) return;
    fetch(`${API_BASE_URL}/api/pendingAcc?arbId=${arbId}`)
      .then((r) => r.json())
      .then((d) => setLots(Array.isArray(d) ? d : JSON.parse(d)))
      .catch(() => {});
  }, [arbId]);

  useEffect(() => {
    if (!arbId) return;
    fetch(`${API_BASE_URL}/api/Meetings?Arb_id=${arbId}`)
      .then((r) => r.json())
      .then((d) => setMeetings(Array.isArray(d) ? d : JSON.parse(d)))
      .catch(() => {});
  }, [arbId]);

  useEffect(() => {
    setBookedSlots([]);
    if (!arbId || !selectedLot || !meetStartTime) return;
    const hearingDate = format(new Date(meetStartTime), "MM/dd/yyyy");
    const query = `arbId=${arbId}&hearingDate=${encodeURIComponent(hearingDate)}&lotNo=${selectedLot.lotNo}&clientId=${selectedLot.clientId}&productId=${selectedLot.productId}`;
    fetch(`${API_BASE_URL}/api/AccBookedSlots?${query}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((slots) => {
        setBookedSlots(slots);
        if (!slots.length) return;
        // Default start: right after the last booked slot, if that is later than the meeting's start
        const lastEnd = slots[slots.length - 1].to;
        setStartTime((current) => (!current || parseTime(current) < parseTime(lastEnd) ? lastEnd : current));
        setEndTime("");
        setTimeDifference(null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arbId, selectedLot, meetStartTime]);

  // ── Time helpers ──────────────────────────────────────────────────────────

  const parseTime = (s) => {
    const [time, mod] = s.split(" ");
    let [h, m] = time.split(":").map(Number);
    if (mod === "PM" && h !== 12) h += 12;
    else if (mod === "AM" && h === 12) h = 0;
    return new Date(1970, 0, 1, h, m);
  };

  // Booked slot (of another lot) that the chosen start–end window overlaps, if any
  const overlappingSlot =
    startTime && endTime
      ? bookedSlots.find(
          (slot) => parseTime(startTime) < parseTime(slot.to) && parseTime(slot.from) < parseTime(endTime)
        )
      : null;

  const validEndTimes = startTime
    ? timeOptions.filter((t) => {
        const diff = (parseTime(t) - parseTime(startTime)) / 3_600_000;
        return diff > 0 && Number.isInteger(diff);
      })
    : [];

  const localTimeFromUTC = (utcStr) => {
    if (!utcStr) return "";
    const d = new Date(utcStr);
    const h = d.getHours(), m = d.getMinutes();
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  const formatMeetingOption = (m) => {
    if (!m.Date) return `Meeting ${m.id}`;
    const d = new Date(m.Date);
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    return `${date} — ${localTimeFromUTC(m.Date)}`;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSelectLot = async (item) => {
    setSelectedLot({ lotNo: item.lotNo, clientId: item.clientId, productId: item.productId });
    setLoading(true);
    // reset configure state
    setSelectedMeetingId(""); setJoinUrl(""); setZoomId(""); setPassword("");
    setMeetStartTime(""); setStartTime(""); setEndTime(""); setTimeDifference(null);
    setRate(""); setNoOfCases(""); setNoOfAwardPassCases("");
    setDistRecords([]); setFormErrors({});
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/RefLots?lotNo=${item.lotNo}&clientId=${item.clientId}&productId=${item.productId}&arbId=${arbId}`
      );
      const d = await r.json();
      setCases(Array.isArray(d) ? d : JSON.parse(d));
      setCasesPage(1);
      setStep(1);
    } catch {
      toast.error("Failed to load cases", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  const handleMeetingSelect = (e) => {
    const id = e.target.value;
    setSelectedMeetingId(id);
    setDistRecords([]);
    setEndTime("");
    setTimeDifference(null);
    setFormErrors((p) => ({ ...p, meeting: "" }));

    if (!id) {
      setJoinUrl(""); setZoomId(""); setPassword(""); setMeetStartTime(""); setStartTime("");
      return;
    }
    const m = meetings.find((x) => String(x.id) === id);
    if (!m) return;
    setJoinUrl(m.Link || "");
    setZoomId(m.Link_id || "");
    setPassword(m.Password || "");
    setMeetStartTime(m.Date || "");
    setStartTime(localTimeFromUTC(m.Date));
  };

  const handleStartTimeChange = (e) => {
    setStartTime(e.target.value);
    setEndTime("");
    setTimeDifference(null);
    setDistRecords([]);
    setFormErrors((p) => ({ ...p, startTime: "", endTime: "" }));
  };

  const handleEndTimeChange = (e) => {
    const val = e.target.value;
    setEndTime(val);
    setFormErrors((p) => ({ ...p, endTime: "" }));
    if (startTime && val) {
      setTimeDifference((parseTime(val) - parseTime(startTime)) / 3_600_000);
    }
  };

  // Auto-redistribute whenever any config field changes — no button click needed.
  // Only runs when all required fields are valid; clears the table otherwise.
  useEffect(() => {
    const allValid =
      selectedMeetingId &&
      startTime &&
      endTime &&
      timeDifference > 0 &&
      rate && !isNaN(rate) && Number(rate) > 0 &&
      noOfCases && Number.isInteger(Number(noOfCases)) && Number(noOfCases) > 0 &&
      noOfAwardPassCases !== "" &&
      Number(noOfAwardPassCases) >= 0 &&
      Number(noOfAwardPassCases) <= Number(noOfCases) &&
      cases.length > 0 &&
      meetStartTime &&
      !overlappingSlot;

    if (!allValid) {
      setDistRecords([]);
      return;
    }

    const total = cases.length;
    const slots = timeDifference;
    const base  = Math.floor(total / slots);
    const extra = total % slots;
    let cur = parseTime(startTime);
    const hearingDate = format(new Date(meetStartTime), "MM/dd/yyyy");
    const dist = [];
    let idx = 0;

    for (let s = 0; s < slots; s++) {
      const slotStart = new Date(cur);
      const slotEnd   = new Date(cur);
      slotEnd.setHours(slotEnd.getHours() + 1);
      const count = base + (s < extra ? 1 : 0);
      for (let i = 0; i < count && idx < total; i++, idx++) {
        dist.push({
          ...cases[idx],
          Hearing_date:      hearingDate,
          Hearing_time_From: format(slotStart, "hh:mm a"),
          Hearing_time_To:   format(slotEnd,   "hh:mm a"),
          Video_link:  joinUrl,
          Link_ID:     zoomId,
          Password:    password,
          No_of_cases: noOfCases,
          Rate:        rate,
          Award_pass_cases: noOfAwardPassCases,
        });
      }
      cur = slotEnd;
    }
    setDistRecords(dist);
    setDistPage(1);
  }, [selectedMeetingId, startTime, endTime, timeDifference, rate, noOfCases, noOfAwardPassCases,
      cases, joinUrl, zoomId, password, meetStartTime, overlappingSlot]);

  const handleGenerateAndSave = async () => {
    setLoading(true);
    try {
      const payload = distRecords.map((item) => ({
        Case_id:           item.Case_id,
        Hearing_date:      item.Hearing_date,
        Hearing_time_from: item.Hearing_time_From,
        Hearing_time_to:   item.Hearing_time_To,
        Video_link:  item.Video_link,
        Link_id:     item.Link_ID,
        Password:    item.Password,
        No_of_cases: noOfCases,
        Rate:        rate,
        Award_pass_cases: noOfAwardPassCases,
      }));

      const saveResp = await fetch(`${API_BASE_URL}/api/acc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // The server first checks every letter can be made (Word file, details, signature); if not, nothing is saved
      if (!saveResp.ok) throw new Error(await readErrorMessage(saveResp));

      const pdfResp = await fetch(
        `${API_BASE_URL}/api/Accletter?lotNo=${selectedLot.lotNo}&clientId=${selectedLot.clientId}&productId=${selectedLot.productId}&arbId=${arbId}`
      );
      if (!pdfResp.ok) throw new Error(await readErrorMessage(pdfResp));

      setPdfUrl(URL.createObjectURL(await pdfResp.blob()));
      setStep(2);
      toast.success("Acceptance letters generated!", { theme: "colored", autoClose: 1500 });
    } catch (e) {
      toast.error(`Failed: ${e.message}`, { theme: "colored", autoClose: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    setLoading(true);
    try {
      const base = `lotNo=${selectedLot.lotNo}&clientId=${selectedLot.clientId}&productId=${selectedLot.productId}&arbId=${arbId}`;
      const r1 = await fetch(`${API_BASE_URL}/api/SaveAccCase?${base}`);
      if (!r1.ok) throw new Error(await r1.text());
      const r2 = await fetch(`${API_BASE_URL}/api/SaveAcc?${base}`);
      if (!r2.ok) throw new Error(await r2.text());
      toast.success("Uploaded successfully!", { theme: "colored", autoClose: 1500 });
      setStep(3);
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`, { theme: "colored", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // ── Derived display data ──────────────────────────────────────────────────

  const filteredLots  = lots.filter((item) =>
    ["Arb_name", "Lots"].some((k) => item[k]?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const visibleLots   = paginate(filteredLots, lotsPage);

  const displayCases  = distRecords.length > 0 ? distRecords : cases;
  const displayPage   = distRecords.length > 0 ? distPage    : casesPage;
  const setDisplayPage = distRecords.length > 0 ? setDistPage : setCasesPage;
  const visibleCases  = paginate(displayCases, displayPage);

  const HIDDEN_KEYS = new Set(["SR_No", "assign_id", "Arbitrator_id", "UPLODED_DATE"]);
  const caseHeaders = displayCases.length > 0
    ? Object.keys(displayCases[0]).filter((k) => !HIDDEN_KEYS.has(k))
    : [];

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container">
      <div className="row mb-3">
        <div className="col-12">
          <CustomStepper
            steps={["Select Lot", "Configure & Assign", "Review & Upload"]}
            activeStep={Math.min(step, 2)}
          />
        </div>
      </div>

      {/* ── STEP 0: Select Lot ─────────────────────────────────────────────── */}
      {step === 0 && (
        <div className="table-container">
          <div className="table-wrapper">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Select Lot</h5>
              <input
                type="text"
                placeholder="Search lots..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setLotsPage(1); }}
                className="form-control"
                style={{ maxWidth: "280px", fontSize: "0.85rem" }}
              />
            </div>

            <table className="responsive-table">
              <thead>
                <tr className="table-info text-center">
                  <th>Sr No</th>
                  <th>Assigned Date</th>
                  <th>Lots</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleLots.length > 0 ? (
                  visibleLots.map((item, i) => (
                    <tr key={i}>
                      <td className="text-center p-1">{(lotsPage - 1) * ROWS + i + 1}</td>
                      <td className="text-center p-1">
                        {item.assignDate
                          ? new Date(item.assignDate).toLocaleDateString("en-GB", {
                              day: "numeric", month: "short", year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="text-center p-1">{item.Lots}</td>
                      <td className="text-center p-1">
                        <button className="custBtn" onClick={() => handleSelectLot(item)}>
                          Show
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">No lots found</td>
                  </tr>
                )}
              </tbody>
            </table>
            <PageButtons total={pageCount(filteredLots)} current={lotsPage} onChange={setLotsPage} />
          </div>
        </div>
      )}

      {/* ── STEP 1: Configure & Assign ────────────────────────────────────── */}
      {step === 1 && (
        <>
          {/* Config panel */}
          <div
            className="p-3 rounded mb-3"
            style={{ background: "#f8f9fa", border: "1px solid #dee2e6" }}
          >
            <div className="row g-2 align-items-end">

              {/* Zoom meeting */}
              <div className="col-md-3">
                <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Zoom Meeting
                </label>
                <Form.Control
                  as="select"
                  value={selectedMeetingId}
                  onChange={handleMeetingSelect}
                  isInvalid={!!formErrors.meeting}
                  style={{ fontSize: "0.85rem" }}
                >
                  <option value="">— Select meeting —</option>
                  {meetings.map((m) => (
                    <option key={m.id} value={m.id}>{formatMeetingOption(m)}</option>
                  ))}
                </Form.Control>
                {formErrors.meeting && (
                  <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "3px" }}>
                    {formErrors.meeting}
                  </div>
                )}
              </div>

              {/* Start Time */}
              <div className="col-md-2">
                <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Start Time
                </label>
                <Form.Control
                  as="select"
                  value={startTime}
                  onChange={handleStartTimeChange}
                  isInvalid={!!formErrors.startTime}
                  style={{ fontSize: "0.85rem" }}
                >
                  <option value="">Select</option>
                  {timeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </Form.Control>
                {(formErrors.startTime || overlappingSlot) && (
                  <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "3px" }}>
                    {formErrors.startTime ||
                      `Overlaps ${overlappingSlot.from} – ${overlappingSlot.to}, already booked. Pick a time that doesn't overlap (e.g. from ${bookedSlots[bookedSlots.length - 1].to}).`}
                  </div>
                )}
              </div>

              {/* End Time */}
              <div className="col-md-2">
                <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  End Time
                </label>
                <Form.Control
                  as="select"
                  value={endTime}
                  onChange={handleEndTimeChange}
                  disabled={!startTime}
                  isInvalid={!!formErrors.endTime}
                  style={{ fontSize: "0.85rem" }}
                >
                  <option value="">Select</option>
                  {validEndTimes.map((t, i) => <option key={i} value={t}>{t}</option>)}
                </Form.Control>
                {formErrors.endTime && (
                  <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "3px" }}>
                    {formErrors.endTime}
                  </div>
                )}
              </div>

              {/* Rate */}
              <div className="col-md-1">
                <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Rate (₹)
                </label>
                <Form.Control
                  type="number"
                  value={rate}
                  onChange={(e) => { setRate(e.target.value); setFormErrors((p) => ({ ...p, rate: "" })); }}
                  isInvalid={!!formErrors.rate}
                  placeholder="1000"
                  style={{ fontSize: "0.85rem" }}
                />
                {formErrors.rate && (
                  <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "3px" }}>
                    {formErrors.rate}
                  </div>
                )}
              </div>

              {/* Ongoing Cases */}
              <div className="col-md-1">
                <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Ongoing
                </label>
                <Form.Control
                  type="number"
                  value={noOfCases}
                  onChange={(e) => { setNoOfCases(e.target.value); setFormErrors((p) => ({ ...p, noOfCases: "" })); }}
                  isInvalid={!!formErrors.noOfCases}
                  placeholder="50"
                  style={{ fontSize: "0.85rem" }}
                />
                {formErrors.noOfCases && (
                  <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "3px" }}>
                    {formErrors.noOfCases}
                  </div>
                )}
              </div>

              {/* Award Pass */}
              <div className="col-md-1">
                <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Award Pass
                </label>
                <Form.Control
                  type="number"
                  value={noOfAwardPassCases}
                  onChange={(e) => { setNoOfAwardPassCases(e.target.value); setFormErrors((p) => ({ ...p, noOfAwardPassCases: "" })); }}
                  isInvalid={!!formErrors.noOfAwardPassCases}
                  placeholder="10"
                  style={{ fontSize: "0.85rem" }}
                />
                {formErrors.noOfAwardPassCases && (
                  <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "3px" }}>
                    {formErrors.noOfAwardPassCases}
                  </div>
                )}
              </div>

              {/* Single CTA — enabled automatically once all fields are valid */}
              <div className="col-md-2">
                <Button
                  className="custBtn w-100"
                  onClick={handleGenerateAndSave}
                  disabled={distRecords.length === 0}
                  style={{ opacity: distRecords.length === 0 ? 0.5 : 1 }}
                >
                  Generate &amp; Save
                </Button>
              </div>

            </div>

            {/* Other lots already booked with this arbitrator on the meeting's date */}
            {bookedSlots.length > 0 && (
              <div
                className="mt-3 p-2 rounded"
                style={{ background: "#fff3cd", color: "#664d03", fontSize: "0.82rem" }}
              >
                <span style={{ fontWeight: 600 }}>Already booked on this date: </span>
                {bookedSlots.map((slot) => `${slot.from} – ${slot.to} (${slot.cases} case${slot.cases > 1 ? "s" : ""}, ${slot.lots.join(", ")})`).join("; ")}
                . This lot's slots must not overlap these (e.g. start from {bookedSlots[bookedSlots.length - 1].to}).
              </div>
            )}

            {/* Zoom link summary strip */}
            {selectedMeetingId && joinUrl && (
              <div
                className="mt-3 pt-2"
                style={{ borderTop: "1px solid #dee2e6", fontSize: "0.82rem", color: "#555" }}
              >
                <span style={{ fontWeight: 600 }}>Link: </span>
                <span style={{ wordBreak: "break-all" }}>{joinUrl}</span>
                <span style={{ fontWeight: 600, marginLeft: "20px" }}>ID: </span>
                <span>{zoomId}</span>
                {password && (
                  <>
                    <span style={{ fontWeight: 600, marginLeft: "20px" }}>Password: </span>
                    <span>{password}</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Cases / Distributed table */}
          <div className="table-container">
            <div className="table-wrapper">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                    {distRecords.length > 0 ? "Time Slots Assigned" : "Cases in Selected Lot"}
                  </span>
                  <span className="ms-2 text-muted" style={{ fontSize: "0.82rem" }}>
                    ({displayCases.length} case{displayCases.length !== 1 ? "s" : ""}
                    {distRecords.length > 0 && timeDifference
                      ? `, ${timeDifference} hour slot${timeDifference > 1 ? "s" : ""}`
                      : ""})
                  </span>
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table className="responsive-table">
                  <thead>
                    <tr className="table-info text-center">
                      <th>Sr No</th>
                      {caseHeaders.map((h) => <th key={h}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCases.map((item, i) => (
                      <tr key={i} className="text-center">
                        <td className="p-1">{(displayPage - 1) * ROWS + i + 1}</td>
                        {caseHeaders.map((h) => (
                          <td key={h} className="p-1">{item[h] ?? "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PageButtons
                total={pageCount(displayCases)}
                current={displayPage}
                onChange={setDisplayPage}
              />
            </div>
          </div>
        </>
      )}

      {/* ── STEP 2: Review & Upload ───────────────────────────────────────── */}
      {step === 2 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Review Acceptance Letters</h5>
            <Button className="custBtn" onClick={handleUpload}>
              Upload
            </Button>
          </div>
          <iframe
            src={pdfUrl}
            style={{ width: "100%", height: "100vh", border: "none" }}
            title="Acceptance Letter PDF"
          />
        </>
      )}

      {/* ── STEP 3: Done ──────────────────────────────────────────────────── */}
      {step === 3 && (
        <div className="d-flex justify-content-center">
          <ClearForm
            message="Acceptance Letter Created Successfully!"
            redirectPath="/arbdashboard/acceptletter/generate_al"
          />
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default GenerateAccLtr;
